import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import routes from './config/routes';
import { ThemeProvider } from '@material-ui/styles';
import theme from './assets/styles/theme';
import './assets/styles/style.css'
import { IServiceUserInfo } from './datasolvr/ServicesFacade';
import axios, { AxiosRequestConfig } from 'axios';
import { IEaterGroupContent, IOfficeContent, IUserContent, IVersionedObject } from './planEatApi/PlanEatFacade';
import { ApplicationUserContext, EaterGroupContext, OfficeContext, PlanEatUserContext } from './config/Context';
import { planEatApi } from './planEatApi/PlanEatApi';
import adminRoutes from './config/adminRoutes';
import { Toaster } from 'react-hot-toast';
import useEventListener from './hooks/useEventListener';
import sysadminRoutes from './config/sysadminRoutes';
import { isNonRestrictedRoute } from './utils/isNonRestrictedRoute';
import { CircularProgress } from '@material-ui/core';
import { planeat_user_group_hierarchy } from './constants/userGroupHierarchy';
import { IRoute } from './interface/interface';

const App: React.FC<{}> = props => {
    // Context state
    const [applicationServiceUser, setApplicationServiceUser] = React.useState<IServiceUserInfo>();
    const [planEatServiceUser, setPlanEatServiceUser] = React.useState<IVersionedObject<IUserContent>>();
    const [eaterGroup,setEaterGroup] = useState<IVersionedObject<IEaterGroupContent>>();
    const [office,setOffice] = useState<IVersionedObject<IOfficeContent>>();
    const [currentPathname, setCurrentPathname] = useState("")
    const [isUserAuthChecked, setUserAuthChecked] = useState(false)

    // Context provider values
    const applicationServiceProviderValue = useMemo(() => ({applicationServiceUser, setApplicationServiceUser}),[applicationServiceUser, setApplicationServiceUser])
    const planEatServiceProviderValue = useMemo(() => ({planEatServiceUser, setPlanEatServiceUser}),[planEatServiceUser, setPlanEatServiceUser])
    const officeProviderValue = useMemo(() => ({office,setOffice}),[office,setOffice])
    const eaterGroupProviderValue = useMemo(() => ({eaterGroup,setEaterGroup}),[eaterGroup,setEaterGroup])

    // Routing
    let location = useLocation();
    let history = useHistory();
    let isMounted = useRef(true);

    // Eventlistner on refocus - check user authenticated
    useEventListener('focus', getUserFromToken)  // Eventlistner on refocus - check user euthen

    function getUserFromToken(){
        let token = localStorage.getItem("token")
        if(token){
            getUser()
        } else if (location.pathname !== "/" && location.pathname !== "/signUp" && location.pathname !== "/forgotLogin" ) {
            postLogout()
        }
    }

    useEffect(() => {
        if(currentPathname !== location.pathname){
            setCurrentPathname(location.pathname)
            setUserAuthChecked(false)
            getUser()
        }
    }, [location.pathname]);



    async function getUser() {
        let token = localStorage.getItem('token')
        

        if(token){
            // Get application user
            planEatApi.postToken(token)
            .then(response => {
                if(!response.data.groups?.includes("unverified_users")){
                    getUserApplicationUser(response.data.userId)
                }
            })
        } else{
            if(isMounted){
                setApplicationServiceUser(undefined)
                setPlanEatServiceUser(undefined)
                postLogout()
            }
        }
    }

    function getUserApplicationUser(userId: string) {
        planEatApi.getServiceUser(userId)
        .then((response) => {
            if(isMounted){
                if(!response.data.content.data_raw?.groups.includes("unverified_users")){
                    if(JSON.stringify(applicationServiceUser) === JSON.stringify(response.data)){
                        setApplicationServiceUser(response.data)
                    }
                    setApplicationServiceUser(response.data)
                    getPlanEatUser(response.data.content.id)
                    if(response.data.content){
                        if(response.data.content.data_raw){
                            localStorage.setItem('groups', response.data.content.data_raw?.groups.toString());
                        }
                    }               
                }
            } 
        })
    }

    function getPlanEatUser(applicationUserId: string) {
        if(applicationUserId){
            planEatApi.getUsers()
            .then((response) => {
                response.data.forEach((user) => {
                    if(user.content.applicationUserId=== applicationUserId){
                        setPlanEatServiceUser(user)
                        localStorage.setItem("userId",user.content.id)
                        if(user.content.eaterGroupId !== undefined && user.content.officeId !== undefined){
                            getEaterGroup(user.content.eaterGroupId)
                            getOffice(user.content.officeId)
                        }
                    }
                })
            })
        }
    }

    const getEaterGroup = async (eaterGroupId: string) => {
        planEatApi.getEaterGroup(eaterGroupId)
        .then((response) => {
            setEaterGroup(response.data)
        })
        .catch((e) => {
            console.log(e)
        })
    }

    const getOffice = async (officeId:string) => {
        var tmpOfficeId = localStorage.getItem("officeId")
        if(tmpOfficeId){
            officeId = tmpOfficeId
        }

        planEatApi.getOffice(officeId)
        .then((response) => {
            localStorage.setItem("officeId",response.data.content.id)
            setOffice(response.data)
        })
        .catch((e) => {
            console.log(e)
        })
    }

    async function postLogout() {
        var token = localStorage.getItem('token');

        if(token){ 
            if(planEatServiceUser){
                await planEatApi.postLogout(token)
                .then((response) =>{
                    console.log(response);
                })
                .catch((e) => {
                    console.log(e)
                })
            }
        } 
        localStorage.clear();
        if(location.pathname !== "/" && location.pathname !== "/signUp" && location.pathname !== "/forgotLogin" && !location.pathname.includes("/onboarding/verify")){
            history.push("")
        }   
    }

    function checkRole (){
        var hasAccess = true;
        var user_groups : string[] = []
        let groups = localStorage.getItem("groups")
        if(groups !== null){
            user_groups = groups.split(",")
        }

        if(applicationServiceUser){
            if(applicationServiceUser.content.data_raw){
                if(applicationServiceUser.content.data_raw.groups){
                    user_groups = applicationServiceUser.content.data_raw.groups
                }
            }
        }   

        let allRoutes : IRoute[] = []
        allRoutes = adminRoutes.concat(routes);
        allRoutes = allRoutes.concat(sysadminRoutes);

        allRoutes.forEach((route) => {
            if(location.pathname=== route.path){
                var userLevel = 0;
                var requiredLevel = 0;

                user_groups.forEach((user_group) => {
                    planeat_user_group_hierarchy.forEach((planeat_group) => {
                        if(user_group=== planeat_group.name){
                            if(planeat_group.hierarchy > userLevel){
                                userLevel = planeat_group.hierarchy
                            }
                        }
                    })
                })
                
                // Find applicationServiceUser Level access
                planeat_user_group_hierarchy.forEach((planeat_group) => {
                    if(route.roleRequired=== planeat_group.name){
                        if(planeat_group.hierarchy > requiredLevel){
                            requiredLevel = planeat_group.hierarchy
                        }
                    }
                })

                if(!(userLevel >= requiredLevel)){
                    postLogout()
                }
            }
        })
        return hasAccess
    }

    // Request interseptor
    axios.interceptors.request.use(
        request => {
         
            if(request.url){
                if(request.url.includes('dishes') || request.url.includes('seatings') || request.url.includes('bookings') || request.url.includes ('users') || request.url.includes("eaterGroups") || request.url.includes("offices") || request.url.includes("companies")){
                request.headers['Datasolvr-Auth'] = localStorage.getItem('token')
                }
            }
            var token = localStorage.getItem('token')
            if(token){
                request.headers['Datasolvr-Auth'] = localStorage.getItem('token')
            }
        
            if(request.data){
                if(request.data.id === ""){
                request.data.id = null;
                }
            }
        
            if(request.url){
                if(!request.url.startsWith('/verify') && !request.url.includes('logOut')){
                request.params['ds_environment'] = process.env.REACT_APP_PLANEAT_ENV
                }
            }
        
            return request
        },
        error => {
            // Set not loading
            postLogout()
            return Promise.reject(error)
        }
    )
    
    // Response header
    axios.interceptors.response.use(function (response) {
        if(response.data){
            if(response.data.token){
                localStorage.setItem('token', response.data.token);
            }   
        }

        return response
    },
        error => {
            // Check if unauthorized
            if(error.response.status === 403){
                postLogout()
            }

            if(error.config.url=== "https://application-service.planeatbooking.com/token" && error.response.status=== 500){
                postLogout()
            }

            return Promise.reject(error)
        }
    )

    return (
        <ThemeProvider theme={theme}>
            <PlanEatUserContext.Provider value={planEatServiceProviderValue}>
                <ApplicationUserContext.Provider value={applicationServiceProviderValue}>
                    <OfficeContext.Provider value={officeProviderValue}>
                        <EaterGroupContext.Provider value={eaterGroupProviderValue}>
                            <div hidden={true}>
                                    <div style={{
                                            position: "fixed",
                                            display: "flex",
                                            justifyContent:'center',
                                            alignItems:'center',
                                            width: "100%",
                                            height: "100%",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: "rgba(0,0,0,0.5)",
                                            zIndex: 2
                                        }}>
                                            <CircularProgress />
                                    </div>
                                </div>
                                <Switch>
                                    {routes.map((route, index) => {
                                        return (
                                            checkRole() ?
                                            <Route
                                                key={route.path}
                                                path={route.path}
                                                exact={route.exact}
                                                component={route.component}
                                            />
                                            :
                                            postLogout()
                                        );
                                    })}
                                </Switch>
                        </EaterGroupContext.Provider>
                    </OfficeContext.Provider>
                </ApplicationUserContext.Provider>
            </PlanEatUserContext.Provider>  
            <Toaster position='bottom-center'/>
        </ThemeProvider>
    );
}

export default App;
